import { PDFDocument, StandardFonts, PageSizes } from "pdf-lib";
import {
  HIJRI_MONTHS, daysInHijriMonth, hijriToGregorian,
} from "@/lib/calendar/hijri";
import { getHijriEvent, getHijriEventsForMonth, HIJRI_EVENTS } from "@/lib/calendar/hijri-events";
import { MONTH_NAMES_ID, dayOfWeek, DAY_NAMES_ID } from "@/lib/calendar/dates";
import { getHolidaysForDate } from "@/data/holidays";
import {
  RED, BLUE, GRAY, TEXT, LIGHT, GREEN, AMBER,
  type BaseOpts, embedLogo, drawBrandHeader, wrapText, drawFooter, drawLegendList,
  DAY_NAMES_SHORT_ID_PDF,
} from "./pdf-shared";

function dayLabel(year: number, month: number, day: number) {
  return DAY_NAMES_ID[dayOfWeek(year, month, day)];
}

function eventColor(type: "hari-besar" | "puasa" | "penting") {
  return type === "hari-besar" ? GREEN : type === "puasa" ? AMBER : GRAY;
}

/** Per-Hijri-month grid PDF (A4 portrait). */
export async function generateHijriMonthPDF(
  hYear: number, hMonth: number, opts: BaseOpts = {},
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(PageSizes.A4);
  const W = page.getWidth(), H = page.getHeight();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);

  const monthName = HIJRI_MONTHS[hMonth - 1];
  const total = daysInHijriMonth(hYear, hMonth);
  const first = hijriToGregorian(hYear, hMonth, 1);
  const last = hijriToGregorian(hYear, hMonth, total);
  const firstDow = dayOfWeek(first.y, first.m, first.d);

  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 22, "Kalender Hijriah Indonesia");
  page.drawText(`${monthName} ${hYear} H`, { x: 30, y: brandBottom - 24, size: 22, font: bold, color: TEXT });
  page.drawText(
    `${first.d} ${MONTH_NAMES_ID[first.m - 1]} ${first.y} - ${last.d} ${MONTH_NAMES_ID[last.m - 1]} ${last.y} (${total} hari)`,
    { x: 30, y: brandBottom - 40, size: 9, font, color: GRAY },
  );

  // Grid
  const top = brandBottom - 56;
  const gridLeft = 30;
  const cellW = (W - 60) / 7;
  const headerH = 22;
  const reservedBottom = 80 + 200;
  const rows = Math.ceil((firstDow + total) / 7);
  const cellH = Math.max(56, Math.min(82, (top - headerH - reservedBottom) / rows));

  for (let i = 0; i < 7; i++) {
    const x = gridLeft + i * cellW;
    page.drawRectangle({ x, y: top - headerH, width: cellW, height: headerH, color: LIGHT });
    page.drawText(DAY_NAMES_SHORT_ID_PDF[i].toUpperCase(), {
      x: x + cellW / 2 - 8, y: top - headerH + 7, size: 9, font: bold,
      color: i === 0 ? RED : TEXT,
    });
  }

  for (let i = 0; i < rows * 7; i++) {
    const col = i % 7;
    const row = Math.floor(i / 7);
    const x = gridLeft + col * cellW;
    const y = top - headerH - (row + 1) * cellH;
    page.drawRectangle({ x, y, width: cellW, height: cellH, borderWidth: 0.5, borderColor: LIGHT });
    const hd = i - firstDow + 1;
    if (hd < 1 || hd > total) continue;
    const g = hijriToGregorian(hYear, hMonth, hd);
    const iso = `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`;
    const hols = getHolidaysForDate(iso);
    const isLibur = hols.some(h => h.type === "libur");
    const ev = getHijriEvent(hMonth, hd);

    page.drawText(String(hd), {
      x: x + 4, y: y + cellH - 14, size: 14, font: bold,
      color: isLibur || col === 0 ? RED : TEXT,
    });
    // Gregorian sub-date
    page.drawText(`${g.d} ${MONTH_NAMES_ID[g.m - 1]}`, {
      x: x + cellW - 50, y: y + cellH - 12, size: 7, font, color: GRAY, maxWidth: 50,
    });

    const labelText = hols[0]?.name ?? ev?.name;
    if (labelText) {
      const color = isLibur ? RED : ev ? eventColor(ev.type) : GRAY;
      const lines = wrapText(labelText, font, 6, cellW - 8).slice(0, 3);
      let ly = y + 6 + (lines.length - 1) * 7;
      for (const ln of lines) {
        page.drawText(ln, { x: x + 4, y: ly, size: 6, font, color });
        ly -= 7;
      }
    }
  }

  // Daftar lengkap
  const listTop = top - headerH - rows * cellH - 16;
  const colW = (W - 60 - 16) / 2;
  const events = getHijriEventsForMonth(hMonth);
  const evItems = events.map(ev => {
    const g = hijriToGregorian(hYear, hMonth, ev.hDay);
    return {
      label: `${ev.hDay} ${monthName} (${dayLabel(g.y, g.m, g.d)}, ${g.d} ${MONTH_NAMES_ID[g.m - 1]} ${g.y}) - ${ev.name}`,
      color: eventColor(ev.type),
    };
  });

  const liburItems: Array<{ label: string; color: typeof RED }> = [];
  const cutiItems: Array<{ label: string; color: typeof BLUE }> = [];
  for (let d = 1; d <= total; d++) {
    const g = hijriToGregorian(hYear, hMonth, d);
    const iso = `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`;
    for (const h of getHolidaysForDate(iso)) {
      const item = { label: `${d} ${monthName} (${g.d} ${MONTH_NAMES_ID[g.m - 1]} ${g.y}) - ${h.name}`, color: h.type === "libur" ? RED : BLUE };
      (h.type === "libur" ? liburItems : cutiItems).push(item as { label: string; color: typeof RED });
    }
  }

  drawLegendList(page, font, bold, evItems, 30, listTop, colW, "Hari Penting Islam");
  let yR = drawLegendList(page, font, bold, liburItems, 30 + colW + 16, listTop, colW, "Libur Nasional Indonesia");
  if (cutiItems.length) drawLegendList(page, font, bold, cutiItems, 30 + colW + 16, yR - 8, colW, "Cuti Bersama");

  drawFooter(page, font, opts.siteUrl ?? `/kalender-hijriah/${hYear}/${hMonth}`);
  return doc.save();
}

/** Hijri Year PDF (A4 landscape, 12 mini grids + halaman 2 daftar). */
export async function generateHijriYearPDF(hYear: number, opts: BaseOpts = {}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PageSizes.A4[1], PageSizes.A4[0]]);
  const W = page.getWidth(), H = page.getHeight();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);

  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 18, "Kalender Hijriah Indonesia");
  page.drawText(`Kalender Hijriah ${hYear} H`, { x: 30, y: brandBottom - 22, size: 18, font: bold, color: TEXT });
  page.drawText("Kalender Islam - Indonesia", { x: W - 200, y: brandBottom - 22, size: 9, font, color: GRAY });

  const cols = 4, rowsM = 3;
  const padX = 30, padY = (H - brandBottom) + 38, gap = 10;
  const monthW = (W - padX * 2 - gap * (cols - 1)) / cols;
  const monthH = (H - padY - 60 - gap * (rowsM - 1)) / rowsM;

  for (let m = 1; m <= 12; m++) {
    const idx = m - 1;
    const cx = padX + (idx % cols) * (monthW + gap);
    const cy = H - padY - Math.floor(idx / cols) * (monthH + gap);
    page.drawRectangle({ x: cx, y: cy - monthH, width: monthW, height: monthH, borderWidth: 0.5, borderColor: LIGHT });
    page.drawText(`${HIJRI_MONTHS[m - 1]}`, { x: cx + 6, y: cy - 14, size: 9, font: bold, color: TEXT });

    const total = daysInHijriMonth(hYear, m);
    const firstG = hijriToGregorian(hYear, m, 1);
    const lastG = hijriToGregorian(hYear, m, total);
    page.drawText(
      `${firstG.d} ${MONTH_NAMES_ID[firstG.m - 1]} - ${lastG.d} ${MONTH_NAMES_ID[lastG.m - 1]} ${lastG.y}`,
      { x: cx + 6, y: cy - 24, size: 6, font, color: GRAY, maxWidth: monthW - 12 },
    );

    const cellW = (monthW - 8) / 7;
    const cellH = (monthH - 36 - 30) / 7;
    const firstDow = dayOfWeek(firstG.y, firstG.m, firstG.d);
    for (let i = 0; i < 7; i++) {
      page.drawText(DAY_NAMES_SHORT_ID_PDF[i][0], {
        x: cx + 4 + i * cellW + cellW / 2 - 2, y: cy - 36, size: 6, font: bold,
        color: i === 0 ? RED : GRAY,
      });
    }
    for (let d = 1; d <= total; d++) {
      const pos = firstDow + d - 1;
      const col = pos % 7;
      const row = Math.floor(pos / 7);
      const x = cx + 4 + col * cellW + cellW / 2 - 4;
      const y = cy - 38 - (row + 1) * cellH + 2;
      const ev = getHijriEvent(m, d);
      const g = hijriToGregorian(hYear, m, d);
      const iso = `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`;
      const isLibur = getHolidaysForDate(iso).some(h => h.type === "libur");
      page.drawText(String(d), {
        x, y, size: 6, font,
        color: col === 0 || isLibur ? RED : ev?.type === "hari-besar" ? GREEN : TEXT,
      });
    }

    // Events at bottom — wrapped, no truncation.
    const events = getHijriEventsForMonth(m);
    const bottomLimitY = cy - monthH + 6;
    let hy = cy - monthH + 30;
    for (const ev of events) {
      if (hy < bottomLimitY) break;
      const text = `${ev.hDay} ${ev.name}`;
      const lines = wrapText(text, font, 5, monthW - 10);
      for (const ln of lines) {
        if (hy < bottomLimitY) break;
        page.drawText(ln, { x: cx + 4, y: hy, size: 5, font, color: eventColor(ev.type) });
        hy -= 6.2;
      }
    }
  }

  drawFooter(page, font, opts.siteUrl ?? `/kalender-hijriah/${hYear}`);

  // Halaman 2 portrait: daftar event Hijri dengan tanggal Masehi
  const p2 = doc.addPage(PageSizes.A4);
  const H2 = p2.getHeight(), W2 = p2.getWidth();
  const brand2 = drawBrandHeader(p2, logo, font, bold, 30, H2 - 22);
  p2.drawText(`Hari Penting Islam ${hYear} H`, { x: 30, y: brand2 - 20, size: 16, font: bold, color: TEXT });

  const allEvents = HIJRI_EVENTS.map(ev => {
    const g = hijriToGregorian(hYear, ev.hMonth, ev.hDay);
    return {
      label: `${dayLabel(g.y, g.m, g.d)}, ${g.d} ${MONTH_NAMES_ID[g.m - 1]} ${g.y} - ${ev.name} (${ev.hDay} ${HIJRI_MONTHS[ev.hMonth - 1]})`,
      color: eventColor(ev.type),
    };
  });
  // Group by month name? Single column wider for readability.
  drawLegendList(p2, font, bold, allEvents, 30, brand2 - 50, W2 - 60, "Daftar lengkap (urut bulan Hijriah)");
  drawFooter(p2, font, opts.siteUrl ?? `/kalender-hijriah/${hYear}`);

  return doc.save();
}

/** Hijri day detail PDF. */
export async function generateHijriDayPDF(
  hYear: number, hMonth: number, hDay: number, opts: BaseOpts = {},
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(PageSizes.A4);
  const W = page.getWidth(), H = page.getHeight();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);

  const g = hijriToGregorian(hYear, hMonth, hDay);
  const monthName = HIJRI_MONTHS[hMonth - 1];
  const ev = getHijriEvent(hMonth, hDay);
  const iso = `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`;
  const hols = getHolidaysForDate(iso);
  const isRed = hols.some(h => h.type === "libur");

  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 22, "Kalender Hijriah Indonesia");
  page.drawText(`${hDay} ${monthName} ${hYear} H`, {
    x: 30, y: brandBottom - 24, size: 22, font: bold, color: isRed ? RED : TEXT,
  });
  page.drawText(`${dayLabel(g.y, g.m, g.d)}, ${g.d} ${MONTH_NAMES_ID[g.m - 1]} ${g.y} M`, {
    x: 30, y: brandBottom - 42, size: 11, font, color: GRAY,
  });

  let y = brandBottom - 70;
  page.drawText("Penanggalan", { x: 30, y, size: 12, font: bold, color: TEXT }); y -= 18;
  const line = (label: string, val: string) => {
    page.drawText(label, { x: 30, y, size: 9, font, color: GRAY });
    page.drawText(val, { x: 170, y, size: 10, font: bold, color: TEXT, maxWidth: W - 200 });
    y -= 18;
  };
  line("Hijriah", `${hDay} ${monthName} ${hYear} H`);
  line("Masehi", `${g.d} ${MONTH_NAMES_ID[g.m - 1]} ${g.y}`);
  line("Hari", dayLabel(g.y, g.m, g.d));

  y -= 8;
  page.drawText("Hari Penting & Libur", { x: 30, y, size: 12, font: bold, color: TEXT }); y -= 18;
  if (!ev && hols.length === 0) {
    page.drawText("Tidak ada hari penting Islam atau libur pada tanggal ini.", { x: 30, y, size: 9, font, color: GRAY });
    y -= 14;
  }
  if (ev) {
    page.drawText(`• [Hari Besar Islam] ${ev.name}`, { x: 30, y, size: 11, font: bold, color: eventColor(ev.type) }); y -= 14;
    for (const ln of wrapText(ev.descId, font, 9, W - 80)) {
      page.drawText(ln, { x: 42, y, size: 9, font, color: GRAY });
      y -= 12;
    }
    y -= 6;
  }
  for (const h of hols) {
    const isLibur = h.type === "libur";
    page.drawText(`• [${isLibur ? "Libur Nasional" : "Cuti Bersama"}] ${h.name}`, {
      x: 30, y, size: 10, font, color: isLibur ? RED : BLUE,
    });
    y -= 14;
  }

  drawFooter(page, font, opts.siteUrl ?? `/kalender-hijriah/${hYear}/${hMonth}`);
  return doc.save();
}